import { useId } from "react"

import { Modal } from "../shared/Modal"
import {
  guestProfileModalCopy,
  type GuestProfileCategory,
} from "./aboutGuestsOptions"

export type AboutGuestsTarget = {
  id: string
  label: string
}

type AboutGuestsFormProps = {
  canAddValue: boolean
  category: GuestProfileCategory
  error: string | null
  isSaving: boolean
  onAddValue: () => void
  onClose: () => void
  onTargetChange: (targetId: string) => void
  onValueChange: (value: string) => void
  remainingOptions: readonly string[]
  selectedTargetId: string | null
  selectedValue: string
  targets: readonly AboutGuestsTarget[]
}

export function AboutGuestsForm({
  canAddValue,
  category,
  error,
  isSaving,
  onAddValue,
  onClose,
  onTargetChange,
  onValueChange,
  remainingOptions,
  selectedTargetId,
  selectedValue,
  targets,
}: AboutGuestsFormProps) {
  const formId = useId()
  const valueSelectId = useId()
  const modalCopy = guestProfileModalCopy[category.field]

  return (
    <Modal
      footer={(
        <>
          <button
            className="h-8 rounded-[4px] border border-[#344149] bg-white px-3 text-xs font-medium leading-4 text-[#344149] hover:bg-[#f5f5f5] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#337ab7] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            disabled={isSaving}
            onClick={onClose}
            type="button"
          >
            Cancel
          </button>
          <button
            className="h-8 rounded-[4px] bg-[#344149] px-3 text-xs font-medium leading-4 text-white hover:bg-[#263138] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#337ab7] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            disabled={!canAddValue}
            form={formId}
            type="submit"
          >
            Add
          </button>
        </>
      )}
      onClose={onClose}
      open
      title={modalCopy.title}
    >
      <form
        className="flex flex-col gap-6"
        id={formId}
        onSubmit={(event) => {
          event.preventDefault()
          onAddValue()
        }}
      >
        <section className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <h3 className="text-base font-medium leading-[19px] text-black">Who is this for?</h3>
            <p className="text-xs font-normal leading-[15px] text-[#6b7078]">
              Choose a guest, or Whole party if it applies to everyone.
            </p>
          </div>

          <div aria-label="Guest" className="flex flex-wrap gap-2.5" role="group">
            {targets.map((target) => {
              const isSelected = selectedTargetId === target.id

              return (
                <button
                  aria-pressed={isSelected}
                  className={[
                    "inline-flex h-8 items-center gap-2 overflow-hidden whitespace-nowrap rounded-[24px] border px-3 py-2 text-[13px] leading-normal focus:outline-none focus-visible:ring-2 focus-visible:ring-[#337ab7] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
                    isSelected
                      ? "border-[#344149] bg-[#d2dadf] font-medium text-[#344149]"
                      : "border-[#d4d4d4] bg-[#f5f5f5] font-normal text-black hover:border-[#344149] hover:bg-[#e7ebee] hover:font-medium hover:text-[#344149]",
                  ].join(" ")}
                  disabled={isSaving}
                  key={target.id || "whole-party"}
                  onClick={() => onTargetChange(target.id)}
                  type="button"
                >
                  {targetChipLabel(target)}
                </button>
              )
            })}
          </div>
        </section>

        <section className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <h3 className="text-base font-medium leading-[19px] text-black">{modalCopy.heading}</h3>
            <p className="text-xs font-normal leading-[15px] text-[#6b7078]">{modalCopy.description}</p>
          </div>

          <label className="sr-only" htmlFor={valueSelectId}>
            Value
          </label>
          <div className="relative">
            <select
              className="h-10 w-full appearance-none rounded-[6px] border border-[#a3a3a3] bg-white px-2.5 pr-9 text-xs font-normal text-black outline-none invalid:text-[#a3a3a3] focus:border-[#337ab7] focus:ring-2 focus:ring-[#337ab7]/20 disabled:cursor-not-allowed disabled:opacity-50"
              disabled={isSaving}
              id={valueSelectId}
              onChange={(event) => onValueChange(event.target.value)}
              required
              value={selectedValue}
            >
              <option value="">Select option</option>
              {remainingOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            <span
              aria-hidden="true"
              className="pointer-events-none absolute right-3 top-1/2 size-2 -translate-y-[65%] rotate-45 border-b border-r border-[#737373]"
            />
          </div>
        </section>

        {error ? (
          <p className="rounded-[4px] border border-[#e0b4b4] bg-[#fff2f2] px-3 py-2 text-sm font-bold text-[#873230]">
            {error}
          </p>
        ) : null}
      </form>
    </Modal>
  )
}

function targetChipLabel(target: AboutGuestsTarget) {
  if (!target.id) return target.label

  return target.label.split(" ")[0] || target.label
}
